import { useIntl } from '@/intl'
import { Day, DayAndMonth, Weekday } from '@/react-pages/Prices/Grid/Day'
import { PriceCell } from '@/react-pages/Prices/Grid/PriceCell'
import { PricesUseCasesContext } from '@/scopes/prices/components/context'
import {
  getPricesSelector,
  pricesResponseSelector,
  selectedPriceIdSelector,
} from '@/scopes/prices/components/selectors'
import { usePricesSelector } from '@/scopes/prices/infrastructure/usePricesSelector'
import { Button, Table, tk, Typography, UnavailableContentDialog } from '@sunwebgroup/ui-components'
import { isEmpty } from 'ramda'
import * as React from 'react'
import { css, styled } from 'styled-components'

const Year = styled(Table.TopLeftHeader)`
  display: flex;
  justify-content: end;
  align-items: end;
  ${Typography} {
    color: ${tk('color_semantic_text_subdued')};
  }
`

const DurationHeader = styled(Table.HeaderColumn)<{ $isHighlighted: boolean, $isSelected: boolean }>`
  display: flex;
  padding: 0px 8px 8px 11px;
  justify-content: end;
  align-items: end;

  ${({ $isHighlighted, $isSelected }) =>
    ($isHighlighted || $isSelected)
    && css`
      background-color: ${tk('color_semantic_bg_strong')};
      &:before {
        border: 1px solid ${tk('color_semantic_border_strong')};
      }
    `}
`

const DayHeader = styled(Table.Header)<{ $isHighlighted: boolean, $isSelected: boolean }>`
  padding: 4px 12px 0 12px;
  ${({ $isHighlighted, $isSelected }) =>
    ($isHighlighted || $isSelected)
    && css`
      background-color: ${tk('color_semantic_bg_strong')};
      &:before {
        border: 1px solid ${tk('color_semantic_border_strong')};
      }
    `}

  ${({ $isSelected }) =>
    $isSelected
    && css`
      ${Weekday} {
        color: ${tk('color_semantic_text')};
      }
      ${DayAndMonth} {
        ${Typography.modVariant('body-sm-bold')}
      }
    `}
`

export function GridNew() {
  const intl = useIntl()
  const { prices, pricesByDuration, pricesByDurationAndDate, dates, year } = usePricesSelector(pricesResponseSelector)
  const { error: isError } = usePricesSelector(getPricesSelector)
  const selectedPriceId = usePricesSelector(selectedPriceIdSelector)
  const { selectPrice } = React.useContext(PricesUseCasesContext)
  const [hoveredDuration, setHoveredDuration] = React.useState<string | null>(null)
  const [hoveredDate, setHoveredDate] = React.useState<string | null>(null)

  const selectedPrice = React.useMemo(
    () => prices?.find(price => price.id === selectedPriceId),
    [prices, selectedPriceId],
  )

  const onHoverStart = React.useCallback((newHoveredDuration: string, date: string) => {
    setHoveredDuration(newHoveredDuration)
    setHoveredDate(date)
  }, [])

  const onHoverEnd = React.useCallback(() => {
    setHoveredDuration(null)
    setHoveredDate(null)
  }, [])

  const onPriceSelect = React.useCallback(
    (newSelectedPriceId: string) => {
      selectPrice(newSelectedPriceId)
    },
    [selectPrice],
  )

  if (isError) {
    return (
      <UnavailableContentDialog
        title={intl.formatMessage({ id: 'prices_error_title' })}
        message={intl.formatMessage({ id: 'prices_error_message' })}
      >
        <Button onClick={() => window.location.reload()}>
          {intl.formatMessage({ id: 'prices_error_reload_button' })}
        </Button>
      </UnavailableContentDialog>
    )
  }

  if (!prices || isEmpty(prices))
    return null

  return (
    <Table.Root data-qa="price-table">
      <Table.Col>
        <Year>
          <Typography $variant="body-sm">{year}</Typography>
        </Year>
        {Object.keys(pricesByDuration).map((duration: string) => (
          <DurationHeader
            key={duration}
            $isHighlighted={duration === hoveredDuration}
            $isSelected={duration === selectedPrice?.duration.toString()}
          >
            <Typography $variant="label">
              {intl.formatMessage({ id: 'prices_grid_duration_header' }, { n_days: duration })}
            </Typography>
          </DurationHeader>
        ))}
      </Table.Col>
      <Table.Body>
        {dates.map(date => (
          <Table.Col key={date}>
            <DayHeader $isHighlighted={date === hoveredDate} $isSelected={date === selectedPrice?.departureDate}>
              <Day date={date} />
            </DayHeader>
            {Object.keys(pricesByDuration).map((duration: string) => (
              <PriceCell
                key={duration + date}
                price={pricesByDurationAndDate[Number(duration)]?.[date]}
                onHoverStart={onHoverStart}
                onHoverEnd={onHoverEnd}
                isHovered={
                  Boolean(pricesByDurationAndDate[Number(duration)]?.[date])
                  && hoveredDuration !== null
                  && hoveredDate !== null
                  && duration === hoveredDuration
                  && date === hoveredDate
                }
                horizontallyHighlighted={
                  hoveredDate !== null && date <= hoveredDate && Number(duration) === Number(hoveredDuration)
                }
                verticallyHighlighted={
                  hoveredDate !== null && date === hoveredDate && Number(duration) <= Number(hoveredDuration)
                }
                onClick={onPriceSelect}
                isSelected={selectedPrice?.departureDate === date && selectedPrice?.duration.toString() === duration}
              />
            ))}
          </Table.Col>
        ))}
      </Table.Body>
    </Table.Root>
  )
}
